import { Box, TextField, RadioGroup, FormControlLabel, Radio, Checkbox, FormControl, FormLabel } from '@mui/material';
import { ArrowUpward, ArrowDownward, ArrowBack, ArrowForward } from '@mui/icons-material';
import { useChartData } from './ChartDataProvider';

const SettingsPanel = () => {
  const { settings, actions } = useChartData();
  
  const setSettingProp = (name, value) => {
    actions.updateSettings({ [name]: value });
  };

  return (
    <Box>
      {/* Grid Lines Settings */}
      <Box sx={{ mb: 2 }}>
        <FormLabel sx={{ mb: 1, display: 'block' }}>Grid Lines</FormLabel>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl sx={{ flex: 1 }}>
            <FormLabel sx={{ fontSize: '0.875rem' }}>Vertical</FormLabel>
            <TextField
              type="number"
              value={settings.thickLineEveryX}
              slotProps={{ htmlInput: { min: 0 } }}
              onChange={(e) => setSettingProp('thickLineEveryX', Number(e.target.value))}
              size="small"
              fullWidth
            />
          </FormControl>
          <FormControl sx={{ flex: 1 }}>
            <FormLabel sx={{ fontSize: '0.875rem' }}>Horizontal</FormLabel>
            <TextField
              type="number"
              value={settings.thickLineEveryY}
              slotProps={{ htmlInput: { min: 0 } }}
              onChange={(e) => setSettingProp('thickLineEveryY', Number(e.target.value))}
              size="small"
              fullWidth
            />
          </FormControl>
        </Box>
      </Box>

      {/* Margin Settings */}
      <Box sx={{ mb: 2 }}>
        <FormLabel sx={{ mb: 1, display: 'block' }}>Chart Margin</FormLabel>
        <FormControl sx={{ minWidth: 120 }}>
          <FormLabel sx={{ fontSize: '0.875rem' }}>Margin Size (px)</FormLabel>
          <TextField
            type="number"
            value={settings.margin || 24}
            slotProps={{ htmlInput: { min: 0, max: 100 } }}
            onChange={(e) => setSettingProp('margin', Number(e.target.value))}
            size="small"
            fullWidth
            helperText="Space allocated for row and column labels (0-100px)"
          />
        </FormControl>
      </Box>

      {/* Cell Shape Settings */}
      <Box sx={{ mb: 2 }}>
        <FormLabel sx={{ mb: 1, display: 'block' }}>Cell Shape</FormLabel>
        <RadioGroup 
          value={settings.yscale} 
          onChange={(e) => setSettingProp('yscale', Number(e.target.value))}
          sx={{ '& .MuiFormControlLabel-root': { marginRight: 3, marginBottom: 0 } }}
          row
        >
          <FormControlLabel value={1} control={<Radio size="small" />} label="Square" />
          <FormControlLabel value={0.8} control={<Radio size="small" />} label="4:5" />
          <FormControlLabel value={0.67} control={<Radio size="small" />} label="2:3" />
        </RadioGroup>
      </Box>
      
      {/* Label Settings */}
      <Box sx={{ mb: 2 }}>
        <FormLabel sx={{ mb: 1, display: 'block' }}>Show Labels</FormLabel>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* Row labels: Left and Right */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControlLabel
              control={
                <Checkbox 
                  checked={settings.showLabels?.left || false} 
                  onChange={(e) => setSettingProp('showLabels', { 
                    ...settings.showLabels, 
                    left: e.target.checked 
                  })} 
                  size="small" 
                />
              }
              label="Left"
            />
            <FormControlLabel
              control={
                <Checkbox 
                  checked={settings.showLabels?.right || false} 
                  onChange={(e) => setSettingProp('showLabels', { 
                    ...settings.showLabels, 
                    right: e.target.checked 
                  })} 
                  size="small" 
                />
              }
              label="Right"
            />
          </Box>
          {/* Column labels: Top and Bottom */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControlLabel
              control={
                <Checkbox 
                  checked={settings.showLabels?.top || false} 
                  onChange={(e) => setSettingProp('showLabels', { 
                    ...settings.showLabels, 
                    top: e.target.checked 
                  })} 
                  size="small" 
                />
              }
              label="Top"
            />
            <FormControlLabel
              control={
                <Checkbox 
                  checked={settings.showLabels?.bottom || false} 
                  onChange={(e) => setSettingProp('showLabels', { 
                    ...settings.showLabels, 
                    bottom: e.target.checked 
                  })} 
                  size="small" 
                />
              }
              label="Bottom"
            />
          </Box>
        </Box>
      </Box>

      {/* Label Direction Settings */}
      <Box sx={{ mb: 2 }}>
        <FormLabel sx={{ mb: 1, display: 'block' }}>Label Direction</FormLabel>
        
        {/* Vertical Direction */}
        <Box sx={{ mb: 1 }}>
          <FormLabel sx={{ fontSize: '0.875rem', mb: 0.5, display: 'block' }}>Vertical</FormLabel>
          <RadioGroup 
            value={settings.labelDirection?.vertical || 'down'} 
            onChange={(e) => setSettingProp('labelDirection', { 
              ...settings.labelDirection, 
              vertical: e.target.value 
            })}
            sx={{ '& .MuiFormControlLabel-root': { marginRight: 3, marginBottom: 0 } }}
            row
          >
            <FormControlLabel 
              value="up" 
              control={<Radio size="small" />} 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <ArrowUpward fontSize="small" />
                  Up
                </Box>
              }
            />
            <FormControlLabel 
              value="down" 
              control={<Radio size="small" />} 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <ArrowDownward fontSize="small" />
                  Down
                </Box>
              }
            />
          </RadioGroup>
        </Box>
        
        {/* Horizontal Direction */}
        <Box>
          <FormLabel sx={{ fontSize: '0.875rem', mb: 0.5, display: 'block' }}>Horizontal</FormLabel>
          <RadioGroup 
            value={settings.labelDirection?.horizontal || 'right'} 
            onChange={(e) => setSettingProp('labelDirection', { 
              ...settings.labelDirection, 
              horizontal: e.target.value 
            })}
            sx={{ '& .MuiFormControlLabel-root': { marginRight: 3, marginBottom: 0 } }}
            row
          >
            <FormControlLabel 
              value="left" 
              control={<Radio size="small" />} 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <ArrowBack fontSize="small" />
                  Left
                </Box>
              }
            />
            <FormControlLabel 
              value="right" 
              control={<Radio size="small" />} 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <ArrowForward fontSize="small" />
                  Right
                </Box>
              }
            />
          </RadioGroup>
        </Box>
      </Box>

      {/* Alternating Label Settings */}
      <Box sx={{ mb: 2 }}>
        <FormLabel sx={{ mb: 1, display: 'block' }}>Label Style</FormLabel>
        <FormControlLabel
          control={
            <Checkbox 
              checked={settings.alternatingLabels || false} 
              onChange={(e) => setSettingProp('alternatingLabels', e.target.checked)} 
              size="small" 
            />
          }
          label="Show labels on alternating sides (for flat knitting)"
        />
        
        {/* Show starting side option only when alternating labels is enabled */}
        {settings.alternatingLabels && (
          <Box sx={{ mt: 1, ml: 3 }}>
            <FormLabel sx={{ fontSize: '0.875rem', mb: 0.5, display: 'block' }}>Start on side</FormLabel>
            <RadioGroup 
              value={settings.alternatingStartSide || 'right'} 
              onChange={(e) => setSettingProp('alternatingStartSide', e.target.value)}
              sx={{ '& .MuiFormControlLabel-root': { marginRight: 3, marginBottom: 0 } }}
              row
            >
              <FormControlLabel 
                value="right" 
                control={<Radio size="small" />} 
                label="Right (odd rows)"
              />
              <FormControlLabel 
                value="left" 
                control={<Radio size="small" />} 
                label="Left (even rows)"
              />
            </RadioGroup>
          </Box>
        )}
      </Box>

      {/* Starting Position Settings */}
      <Box sx={{ mb: 2 }}>
        <FormLabel sx={{ mb: 1, display: 'block' }}>Starting Position</FormLabel>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl sx={{ flex: 1 }}>
            <FormLabel sx={{ fontSize: '0.875rem' }}>Row</FormLabel>
            <TextField
              type="number"
              value={settings.startRow}
              slotProps={{ htmlInput: { min: 0 } }}
              onChange={(e) => setSettingProp('startRow', Number(e.target.value))}
              size="small"
              fullWidth
            />
          </FormControl>
          <FormControl sx={{ flex: 1 }}>
            <FormLabel sx={{ fontSize: '0.875rem' }}>Column</FormLabel>
            <TextField
              type="number"
              value={settings.startCol}
              slotProps={{ htmlInput: { min: 0 } }}
              onChange={(e) => setSettingProp('startCol', Number(e.target.value))}
              size="small"
              fullWidth
            />
          </FormControl>
        </Box>
      </Box>
    </Box>
  );
};

export default SettingsPanel;
